import { create } from "zustand";
import { persist, createJSONStorage } from "zustand/middleware";
import { UserResponse } from "@/api/auth/types";
import { resetAllStores } from "./resettable-store";

export const AUTH_STORAGE_KEY = "koachy-auth-storage";

// The window has no upper bound: the API also refreshes an already expired
// access token, as long as the refresh token itself is still good.
const REFRESH_BUFFER_MS = 5 * 60 * 1000;

export interface AuthSession {
  accessToken: string;
  refreshToken: string;
  expiresIn: number;
  expiresAt: number;
  userType: "staff";
  userProfile: UserResponse["session"]["userProfile"];
}

interface AuthState {
  session: AuthSession | null;
  isAuthenticated: () => boolean;
  token: () => string | null;
  shouldRefreshToken: () => boolean;
  setSession: (sessionData: UserResponse["session"]) => void;
  getUserProfile: () =>
    | UserResponse["session"]["userProfile"]["staffMember"]
    | null;
  clearSession: () => void;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      session: null,

      isAuthenticated: () => {
        const { session } = get();
        if (!session) return false;

        // An expired access token is still recoverable while there is a refresh
        // token: what ends the session is the API rejecting it, not the clock.
        return Date.now() < session.expiresAt || !!session.refreshToken;
      },

      token: () => {
        const { session } = get();
        return session?.accessToken || null;
      },

      // Not the negation of isAuthenticated(): when the two lined up exactly,
      // the request interceptor only asked for a refresh where it was refused.
      shouldRefreshToken: () => {
        const { session } = get();
        if (!session?.refreshToken) return false;

        return Date.now() >= session.expiresAt - REFRESH_BUFFER_MS;
      },

      setSession: (sessionData) => {
        const expiresAt = Date.now() + sessionData.expiresIn * 1000;
        const session: AuthSession = {
          ...sessionData,
          expiresAt,
        } as AuthSession;

        set({ session });
      },

      getUserProfile: () => {
        const { session } = get();
        return session?.userProfile?.staffMember || null;
      },

      clearSession: () => {
        set({ session: null });
        resetAllStores();
      },
    }),
    {
      name: AUTH_STORAGE_KEY,
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({ session: state.session }),
    },
  ),
);
